import type { ReactNode } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { SMALL_ICON_BUTTON_CLASS } from '@/lib/styles';
import { HistoryTable } from './HistoryTable';
import { HistoryTabs } from './HistoryTabs';
import type { HistoryTab } from './types';

interface HistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Brand-coloured line under the title, e.g. "#27 - Arun Prakash · P2701". */
  subtitle?: ReactNode;
  tabs: HistoryTab[];
  activeId: string;
  onTabChange: (id: string) => void;
  loading?: boolean;
  /** Export is not built yet; the button shows and does nothing until this is passed. */
  onExport?: () => void;
}

/** Full-height View All modal shared by every history and long list. */
export function HistoryModal({
  open,
  onOpenChange,
  title,
  subtitle,
  tabs,
  activeId,
  onTabChange,
  loading = false,
  onExport,
}: HistoryModalProps) {
  const tab = tabs.find((item) => item.id === activeId) ?? tabs[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[calc(100dvh-48px)] w-[min(1100px,calc(100%-2rem))] max-w-none flex-col gap-0 overflow-hidden rounded-xl p-0 sm:max-w-none"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div className="min-w-0">
            <DialogTitle className="text-base font-semibold text-gray-900">{title}</DialogTitle>
            <DialogDescription className="mt-0.5 text-sm font-medium text-[#4369a0]">{subtitle}</DialogDescription>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" onClick={onExport} className="h-9 border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className={SMALL_ICON_BUTTON_CLASS} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {tabs.length > 1 && (
          <div className="shrink-0 border-b border-gray-200 px-6">
            <HistoryTabs tabs={tabs} activeId={tab.id} onChange={onTabChange} />
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto bg-gray-50/80 px-6 py-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : (
            <>
              <p className="mb-2.5 text-[13px] text-gray-500">
                {tab.rows.length} {tab.rows.length === 1 ? 'record' : 'records'}
              </p>
              <HistoryTable columns={tab.columns} rows={tab.rows} stickyHeader empty={tab.empty} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
